import React, { useState, useEffect } from 'react'
import {
  BookOpen,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  TrendingDown,
  TrendingUp,
  BarChart3
} from 'lucide-react'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Table from '../../components/common/Table'
import Card from '../../components/common/Card'
import Select from '../../components/common/Select'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { evaluacionService } from '../../services/evaluacionService'
import { usePermissions } from '../../hooks/usePermissions'

const Evaluaciones = () => {
  const { canView, canCreate, canEdit, canDelete } = usePermissions()
  const [evaluaciones, setEvaluaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [grupoFilter, setGrupoFilter] = useState('')
  const [periodoFilter, setPeriodoFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalEvaluaciones, setTotalEvaluaciones] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [editingEvaluacion, setEditingEvaluacion] = useState(null)
  const [viewingEvaluacion, setViewingEvaluacion] = useState(null)
  const [datosFormulario, setDatosFormulario] = useState(null)
  const [stats, setStats] = useState({
    total: 0,
    promedio: 0
  })

  const { register, handleSubmit, reset, formState: { errors }, watch } = useForm({
    mode: 'onChange'
  })

  useEffect(() => {
    fetchDatosFormulario()
  }, [])

  useEffect(() => {
    fetchEvaluaciones()
  }, [currentPage, perPage, searchTerm, grupoFilter, periodoFilter])

  // Reset a página 1 cuando cambian filtros
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, grupoFilter, periodoFilter])

  const fetchDatosFormulario = async () => {
    try {
      const response = await evaluacionService.getDatosFormulario()
      if (response.success) {
        setDatosFormulario(response.data)
      }
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error)
    }
  }

  const fetchEvaluaciones = async () => {
    try {
      setLoading(true)
      const response = await evaluacionService.getEvaluaciones({
        page: currentPage,
        per_page: perPage,
        search: searchTerm,
        grupo_id: grupoFilter || undefined,
        periodo_id: periodoFilter || undefined
      })
      
      if (response.success && response.data) {
        setEvaluaciones(response.data.data || [])
        setTotalPages(response.data.last_page || 1)
        setTotalEvaluaciones(response.data.total || 0)
        console.log('Evaluaciones cargadas:', response.data.data)
        const evaluaciones = response.data?.data || [];
        console.log('Evaluaciones :', evaluaciones)
        const promedio = evaluaciones.length? (
            evaluaciones.reduce((sum, e) => sum + parseFloat(e.porcentaje || 0), 0) 
            / evaluaciones.length
          ).toFixed(1)
          : 0;
        
        setStats({
          total: response.data.total || 0,
          promedio: promedio
        })
      } else {
        toast.error(response.message || 'Error al cargar evaluaciones')
        setEvaluaciones([])
        setTotalPages(1)
        setTotalEvaluaciones(0)
      }
    } catch (error) {
      toast.error('Error al cargar evaluaciones')
      setEvaluaciones([])
      setTotalPages(1)
      setTotalEvaluaciones(0)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (evaluacion) => {
    setEditingEvaluacion(evaluacion)
    reset({
      nombre: evaluacion.nombre,
      porcentaje: evaluacion.porcentaje,
      grupo_id: evaluacion.grupo_id,
      periodo_id: evaluacion.periodo_id,
      descripcion: evaluacion.descripcion || ''
    })
    setShowModal(true)
  }

  const handleView = (evaluacion) => {
    setViewingEvaluacion(evaluacion)
    setShowViewModal(true)
  }

  const handleDelete = async (evaluacion) => {
    if (!window.confirm(`¿Está seguro de eliminar la evaluación "${evaluacion.nombre}"?`)) {
      return
    }

    try {
      const response = await evaluacionService.deleteEvaluacion(evaluacion.id)
      if (response.success) {
        toast.success(response.message)
        fetchEvaluaciones()
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      toast.error('Error al eliminar evaluación')
    }
  }

  const handleNew = () => {
    setEditingEvaluacion(null)
    reset({
      nombre: '',
      porcentaje: '',
      grupo_id: '',
      periodo_id: '',
      descripcion: ''
    })
    setShowModal(true)
  }

  const onSubmit = async (data) => {
    if (!data.nombre || data.nombre.trim() === '') {
      toast.error('El nombre es obligatorio')
      return
    }

    if (!data.porcentaje || data.porcentaje <= 0) {
      toast.error('El porcentaje debe ser mayor a 0')
      return
    }

    if (!data.grupo_id) {
      toast.error('El grupo es obligatorio')
      return
    }

    if (!data.periodo_id) {
      toast.error('El período es obligatorio')
      return
    }

    try {
      setLoading(true)
      let response

      if (editingEvaluacion) {
        response = await evaluacionService.updateEvaluacion(editingEvaluacion.id, data)
      } else {
        response = await evaluacionService.createEvaluacion(data)
      }

      if (response.success) {
        toast.success(response.message || (editingEvaluacion ? 'Evaluación actualizada' : 'Evaluación creada'))
        setShowModal(false)
        reset()
        setEditingEvaluacion(null)
        await fetchEvaluaciones()
      } else {
        toast.error(response.message || 'Error al guardar evaluación')
        if (response.errors) {
          Object.values(response.errors).flat().forEach(err => toast.error(err))
        }
      }
    } catch (error) {
      toast.error('Error al guardar evaluación')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      sortable: true,
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
            <BookOpen className="h-5 w-5" />
          </div>
          <span className="font-medium text-gray-900 dark:text-gray-100">{row.nombre}</span>
        </div>
      )
    },
    {
      key: 'grupo',
      label: 'Grupo',
      render: (row) => (
        <span className="text-gray-600 dark:text-gray-400">
          {row.grupo?.nombre || 'N/A'}
        </span>
      )
    },
    {
      key: 'porcentaje',
      label: 'Porcentaje',
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400">
          {row.porcentaje}%
        </span>
      )
    },
    {
      key: 'periodo',
      label: 'Período',
      render: (row) => (
        <span className="text-gray-600 dark:text-gray-400">
          {row.periodo?.nombre || 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center space-x-2">
          {canView('programas') && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleView(row)
              }}
              className="p-2 rounded-xl text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              title="Ver detalles"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {canEdit('programas') && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleEdit(row)
              }}
              className="p-2 rounded-xl text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 transition-colors"
              title="Editar"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {canDelete('programas') && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDelete(row)
              }}
              className="p-2 rounded-xl text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
              title="Eliminar"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4 mb-4 sm:mb-0">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold gradient-text">Evaluaciones</h1>
            <p className="text-gray-600 dark:text-gray-400">Gestiona las evaluaciones por grupo y período</p>
          </div>
        </div>
        {canCreate('programas') && (
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={handleNew}
          >
            Nueva Evaluación
          </Button>
        )}
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Card className="gradient" shadow="glow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Total Evaluaciones</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-glow">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
        
        <Card className="gradient" shadow="glow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Promedio Porcentaje</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.promedio}%</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-success-500 to-success-600 rounded-xl flex items-center justify-center shadow-glow">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="gradient" shadow="glow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar evaluaciones..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              className="pl-10"
            />
          </div>
          
          {datosFormulario?.grupos && (
            <select
              value={grupoFilter}
              onChange={(e) => {
                setGrupoFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos los grupos</option>
              {datosFormulario.grupos.map(g => (
                <option key={g.id} value={g.id}>{g.codigo}</option>
              ))}
            </select>
          )}



          {datosFormulario?.periodos && (
            <select
              value={periodoFilter}
              onChange={(e) => {
                setPeriodoFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos los períodos</option>
              {datosFormulario.periodos.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          )}
        </div>
      </Card>

      {/* Tabla */}
      <Card className="gradient" shadow="glow-lg">
        <Table
          columns={columns}
          data={evaluaciones}
          loading={loading}
          emptyMessage="No se encontraron evaluaciones"
          hover
          striped
          currentPage={currentPage}
          totalPages={totalPages}
          perPage={perPage}
          onPageChange={(page) => setCurrentPage(page)}
          onPerPageChange={(newPerPage) => {
            setPerPage(newPerPage)
            setCurrentPage(1)
          }}
        />
      </Card>

      {/* Modal Crear/Editar */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingEvaluacion(null)
          reset()
        }}
        title={editingEvaluacion ? 'Editar Evaluación' : 'Nueva Evaluación'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre *
            </label>
            <Input
              type="text"
              {...register('nombre', { required: 'El nombre es requerido' })}
              placeholder="Ej: Parcial 1"
              error={errors.nombre?.message}
            />
          </div>

          <div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Grupo {editingEvaluacion && <span className="text-xs text-gray-500">(bloqueado)</span>} *
              </label>
              <select
                {...register('grupo_id', { required: 'El grupo es requerido' })}
                disabled={!!editingEvaluacion}
                className={`w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 ${editingEvaluacion ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <option value="">Seleccionar grupo</option>
                {datosFormulario?.grupos?.map(g => (
                  <option key={g.id} value={g.id}>{g.nombre} ({g.modulo.nombre})</option>
                ))}
              </select>
              {errors.grupo_id && <p className="text-error-600 text-sm">{errors.grupo_id.message}</p>}
            </div>


          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Período *
              </label>
              <select
                {...register('periodo_id', { required: 'El período es requerido' })}
                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Seleccionar período</option>
                {datosFormulario?.periodos?.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
              {errors.periodo_id && <p className="text-error-600 text-sm">{errors.periodo_id.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Porcentaje *
              </label>
              <Input
                type="number"
                {...register('porcentaje', { 
                  required: 'El porcentaje es requerido',
                  min: { value: 1, message: 'Mínimo 1%' },
                  max: { value: 100, message: 'Máximo 100%' }
                })}
                placeholder="30"
                error={errors.porcentaje?.message}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Descripción
            </label>
            <Input
              type="text"
              {...register('descripcion')}
              placeholder="Descripción de la evaluación"
              error={errors.descripcion?.message}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowModal(false)
                setEditingEvaluacion(null)
                reset()
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading}
            >
              {editingEvaluacion ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Ver */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Detalles de la Evaluación"
        size="md"
      >
        {viewingEvaluacion && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Nombre</p>
              <p className="text-gray-900 dark:text-gray-100">{viewingEvaluacion.nombre}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Grupo</p>
              <p className="text-gray-900 dark:text-gray-100">{viewingEvaluacion.grupo?.codigo || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Período</p>
                <p className="text-gray-900 dark:text-gray-100">{viewingEvaluacion.periodo?.nombre || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Porcentaje</p>
                <p className="text-gray-900 dark:text-gray-100">{viewingEvaluacion.porcentaje}%</p>
              </div>
            </div>
            {viewingEvaluacion.descripcion && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Descripción</p>
                <p className="text-gray-900 dark:text-gray-100">{viewingEvaluacion.descripcion}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Evaluaciones
